import { ILoader } from '../../base/loader/iLoader.js';
import type { PreprocessedTemplate } from '../model/index.js';
import type { DynamicResults } from '../../model/dynamicResults.js';
import type { JsonObject } from '../../app/json/jsonValue.js';
import type { PhaseTiming } from '../../base/timing/phaseTiming.js';

/**
 * Result returned by getTemplate
 */
export interface GetTemplateResult {
    template: PreprocessedTemplate;
    json: JsonObject | null;
    resolvedTemplateName: string;
    resolvedAppSite: string;
}

export interface GetTemplateWithViewResult extends GetTemplateResult {
    viewHtml: string;
    latePlaceholders: string[];
}

/**
 * ILoaderPreProcess - Loader interface specifically for EnginePreProcess
 *
 * This is a marker interface (like C# interfaces) - no method bodies.
 * Extends ILoader with PreProcess-specific getTemplate() return type.
 * Actual implementation is in LoaderPreProcess and loaderPreProcess_*.ts partial files.
 *
 * Expected method (in addition to ILoader methods):
 *
 * @method setDynamicResults(dynamicResults) - Sets the DynamicResults instance for the current merge
 * @method getTemplate(appSite, currentTemplateName, appFileOrPlaceholderOrSlotholderName, appView?, flowStack?, configJson?)
 *   Gets a template's preprocessed HTML and JSON with AppView and SearchAppSites fallback support.
 *   Returns: {template: PreprocessedTemplate, json: JsonObject|null, resolvedTemplateName: string, resolvedAppSite: string}
 *
 * @method applyDynamicViewHandlers(appSite, appView, currentTemplateName, componentName, componentDepth, componentHtml, componentJson, resolvedAppSite, resolvedComponentName, configJson?)
 *   Applies DynamicView handlers to wrap/modify component HTML. Called by Engine after getTemplate.
 *   Returns: { html: string, newPlaceholders: string[] }
 *     - html: modified HTML after all handlers ran
 *     - newPlaceholders: {{Name}} placeholder names discovered in the post-handler HTML, in order
 *       of first appearance. Engine resolves these as late-bound components without re-scanning.
 *
 * IMPORTANT CONTRACT:
 * - currentTemplateName MUST be a clean template name WITHOUT AppSite prefix
 * - Example: "MainContent" NOT "JsonRule5A_MainContent"
 */
export abstract class ILoaderPreProcess extends ILoader {
    abstract setDynamicResults(dynamicResults: DynamicResults): void;
    abstract getTemplate(appSite: string, currentTemplateName: string, appFileOrPlaceholderOrSlotholderName: string, timing: PhaseTiming, appView?: string | null, flowStack?: string[] | null, configJson?: JsonObject | null): GetTemplateResult;
    abstract getTemplateWithView(appSite: string, currentTemplateName: string, componentName: string, timing: PhaseTiming, appView?: string | null, flowStack?: string[] | null, configJson?: JsonObject | null, clientMode?: boolean): GetTemplateWithViewResult;
    abstract applyViewToTemplate(appSite: string, currentTemplateName: string, componentName: string, timing: PhaseTiming, appView: string | null, flowStack: string[] | null, configJson: JsonObject | null, result: GetTemplateResult, clientMode?: boolean): GetTemplateWithViewResult;
    abstract applyDynamicViewHandlers(appSite: string, appView: string, currentTemplateName: string, componentName: string, componentDepth: number, componentHtml: string, componentJson: JsonObject | null, resolvedAppSite: string, resolvedComponentName: string, configJson: JsonObject | null, timing: PhaseTiming): { html: string; newPlaceholders: string[] };
}
